// WhatsAppInbox.jsx — CONTAINER do painel de conversas do WhatsApp (dono da lógica). Busca na API, monta
// { vm, actions } (contrato: shell/whatsappInbox.vm.js @v2) e renderiza a View do Claude Design, que chega por prop
// (`View`) — até a volta do CD ela não existe e o Layout nem monta este container (INBOX_VIEW_READY).
// Plano: docs/PLANO-EXECUCAO-CONVERSAS-WHATSAPP.md, TODO 4.5 e revisão 2.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import whatsappService from '../services/whatsappService';
import { buildInboxVm, mergeMessages } from './whatsappInboxVmBuilder';
import { INBOX_UNREAD_QUERY_KEY, INBOX_CONVERSATIONS_QUERY_KEY } from './whatsappInboxQueryKeys';
import { trackEvent } from '../utils/tracking';

const LIST_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const OPEN_CONVERSATION_REFRESH_MS = 20_000;   // conversa aberta: mensagem nova aparece sozinha

export default function WhatsAppInbox({ open, onClose, View }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Lista: busca + "carregar mais" ─────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const conversationsQuery = useInfiniteQuery({
    queryKey: [...INBOX_CONVERSATIONS_QUERY_KEY, searchQuery],
    queryFn: ({ pageParam }) => whatsappService.listConversations({ limit: LIST_PAGE_SIZE, before: pageParam, q: searchQuery }),
    initialPageParam: null,
    // Cursor = horário da última mensagem da última conversa mostrada (a API devolve as anteriores a ele).
    getNextPageParam: (last) => (last?.hasMore ? last.items?.at(-1)?.lastMessage?.createdAt ?? undefined : undefined),
    enabled: !!open,
    refetchInterval: open ? 60_000 : false,
    refetchOnWindowFocus: true,
  });
  const conversationItems = conversationsQuery.data
    ? conversationsQuery.data.pages.flatMap((p) => p?.items ?? [])
    : null;

  // ── Conversa aberta ───────────────────────────────────────────────────────
  const [selectedKey, setSelectedKey] = useState(null);
  const [messages, setMessages] = useState(null);        // acumuladas, mais antiga → mais nova (forma da API)
  const [hasMore, setHasMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messagesError, setMessagesError] = useState(false);

  const resetConversation = useCallback(() => {
    setSelectedKey(null);
    setMessages(null);
    setHasMore(false);
    setMessagesError(false);
  }, []);

  // Fechar o painel volta para a lista (e limpa a busca) na próxima abertura.
  useEffect(() => {
    if (!open) {
      resetConversation();
      setSearchInput('');
    }
  }, [open, resetConversation]);

  // P2: abrir a conversa (ou chegar mensagem nova com ela aberta) marca como lida. Best-effort.
  const markRead = useCallback(async (key) => {
    try {
      await whatsappService.markConversationRead(key);
      queryClient.invalidateQueries({ queryKey: INBOX_UNREAD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: INBOX_CONVERSATIONS_QUERY_KEY });
    } catch (err) {
      console.warn('[WhatsAppInbox] não consegui marcar a conversa como lida', err);
    }
  }, [queryClient]);

  const openConversation = useCallback(async (key) => {
    setSelectedKey(key);
    setMessages(null);
    setHasMore(false);
    setMessagesError(false);
    setMessagesLoading(true);
    try {
      const page = await whatsappService.listConversationMessages(key);
      setMessages(page?.items ?? []);
      setHasMore(!!page?.hasMore);
    } catch {
      setMessagesError(true);
      return;
    } finally {
      setMessagesLoading(false);
    }
    markRead(key);
  }, [markRead]);

  // Conversa aberta se atualiza sozinha: busca a página mais nova e junta com o que está na tela (mensagem nova
  // entra; tique que mudou é trocado). Se chegou recebida nova, marca como lida — o dono está olhando.
  const refreshing = useRef(false);
  useEffect(() => {
    if (!open || !selectedKey || messagesLoading || messagesError) return undefined;
    const id = setInterval(async () => {
      if (refreshing.current) return;
      refreshing.current = true;
      try {
        const page = await whatsappService.listConversationMessages(selectedKey);
        const novas = page?.items ?? [];
        setMessages((prev) => mergeMessages(prev, novas));
        if (novas.some((m) => m.direction === 'in' && m.unread)) markRead(selectedKey);
      } catch {
        // silencioso: a próxima volta tenta de novo; a conversa que já está na tela continua
      } finally {
        refreshing.current = false;
      }
    }, OPEN_CONVERSATION_REFRESH_MS);
    return () => clearInterval(id);
  }, [open, selectedKey, messagesLoading, messagesError, markRead]);

  const loadMore = useCallback(async () => {
    if (!selectedKey || !messages?.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await whatsappService.listConversationMessages(selectedKey, { before: messages[0].createdAt });
      setMessages((prev) => mergeMessages(prev, page?.items ?? []));
      setHasMore(!!page?.hasMore);
    } catch {
      setMessagesError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [selectedKey, messages, loadingMore]);

  if (!View) return null;

  const vm = buildInboxVm({
    open: !!open,
    conversationItems,
    conversationsLoading: conversationsQuery.isLoading,
    conversationsError: conversationsQuery.isError,
    hasMoreConversations: !!conversationsQuery.hasNextPage,
    loadingMoreConversations: conversationsQuery.isFetchingNextPage,
    searchQuery: searchInput,
    selectedKey,
    messages,
    hasMore,
    messagesLoading,
    loadingMore,
    messagesError,
  });

  const actions = {
    onClose: () => onClose?.(),
    onOpenConversation: (key) => {
      trackEvent('whatsapp_inbox_conversation_open', {});
      openConversation(key);
    },
    onBackToList: resetConversation,
    onLoadMore: loadMore,
    onLoadMoreConversations: () => {
      if (conversationsQuery.hasNextPage && !conversationsQuery.isFetchingNextPage) conversationsQuery.fetchNextPage();
    },
    onSearch: (q) => setSearchInput(q ?? ''),
    onOpenPerson: (href) => {
      onClose?.();
      navigate(href);
    },
    onReplyOnWhatsApp: (href) => {
      if (!href) return;
      trackEvent('whatsapp_inbox_reply_on_whatsapp', {});
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    onSeeAll: () => {
      onClose?.();
      navigate('/integrations');
    },
    onRetry: () => (selectedKey ? openConversation(selectedKey) : conversationsQuery.refetch()),
  };

  return <View vm={vm} actions={actions} />;
}
